import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ValidationErrorsTable, {SpreadsheetType} from 'bulk-import/panes/ValidationErrorsTable';
import {SelectedSchemaIds, TaskState} from 'bulk-import/SpreadsheetBulkImportConstants';
import {Status} from 'constants/StatusConstants';
import {ErrorHandlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  AutomationFileErrorInfo,
  AutomationInputFile,
  AutomationOutputFile,
  AutomationOutputFileType,
  AutomationTransformFile,
  ProcessingSummaryStatistics,
} from 'lab-automation/automation-files/LabAutomationConstants';
import {LabAutomationTransformType} from 'lab-automation/settings/RunSchemaSpecConstants';
import {EditorPropertiesContext, EditorPropertiesContextInterface} from 'notebook/notes/core-ui/NoteContext';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip from 'ui-library/bs/Tooltip';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import {readableTime} from 'util/DateUtil';
import * as modals from 'util/Modals';
import {snakeToCapital} from 'util/NameUtil';

import {
  AutomationFileErrorsModalQuery,
  AutomationFileErrorsModalQueryVariables,
} from './__generated/AutomationFileErrorsModalQuery';
import * as AutomationFileDAO from './AutomationFileDAO';
import AutomationFileProgressBar from './AutomationFileProgressBar';
import UploadAutomationOutputFileButton from './UploadAutomationOutputFileButton';

export const AUTOMATION_FILE_ERRORS_MODAL_QUERY = gql`
  query AutomationFileErrorsModalQuery($tagSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: LAB_AUTOMATION) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        ...TagSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;
export function isAutomationInputFile(
  automationFile: AutomationOutputFile | AutomationInputFile | AutomationTransformFile
): automationFile is AutomationInputFile {
  return automationFile.hasOwnProperty('automationInputFileConfig');
}

export function isAutomationOutputFile(
  automationFile: AutomationOutputFile | AutomationInputFile | AutomationTransformFile
): automationFile is AutomationOutputFile {
  return automationFile.hasOwnProperty('automationOutputFileConfig');
}

export function isAutomationProcessingFile(
  automationFile: AutomationOutputFile | AutomationInputFile | AutomationTransformFile
): automationFile is AutomationTransformFile {
  return automationFile.id != null && APIUtil.isAutomationTransformFile(automationFile.id);
}

function getSchemaIds(
  automationFile: AutomationOutputFile | AutomationInputFile | AutomationTransformFile
): SelectedSchemaIds {
  if (!isAutomationOutputFile(automationFile)) {
    return {};
  }
  // Only the tagSchemaId is needed for displaying errors
  return {
    tagSchemaId: automationFile.automationOutputFileConfig.tagSchemaInfo?.tagSchemaId,
  };
}

interface AutomationFileErrorsProps {
  errors: AutomationFileErrorInfo | null;
  schemaIds: SelectedSchemaIds;
}

export function AutomationFileErrors(props: AutomationFileErrorsProps): JSX.Element | null {
  const {errors, schemaIds} = props;
  if (errors == null) {
    return null;
  }
  // eslint-disable-next-line benchlint/no-in-operator
  if ('topLevelErrors' in errors) {
    return (
      <>
        {errors.topLevelErrors.map((error, i) => (
          <div key={i}>
            {error.errorMessage.split('\n').map((errorParagraph, j) => (
              <div key={j}>
                <span>{errorParagraph}</span>
                <br />
                &nbsp;
              </div>
            ))}
          </div>
        ))}
      </>
    );
  } else {
    return (
      <ErrorHandlingQuery<AutomationFileErrorsModalQuery, AutomationFileErrorsModalQueryVariables>
        query={AUTOMATION_FILE_ERRORS_MODAL_QUERY}
        fetchPolicy="cache-and-network"
        skip={!schemaIds.tagSchemaId}
        variables={{
          tagSchemaId: schemaIds.tagSchemaId || '',
        }}
      >
        {({loading: isLoading}) => {
          if (isLoading) {
            return <HorizontalThrobber />;
          }
          return (
            <ValidationErrorsTable
              spreadsheetType={SpreadsheetType.SPREADSHEET}
              errorData={{state: TaskState.COMPLETED, ...errors}}
              resolvableErrorStatuses={{}}
              nextButtonText=""
              selectedTagSchemaId={schemaIds.tagSchemaId}
            />
          );
        }}
      </ErrorHandlingQuery>
    );
  }
}
const STATUS_ICONS: {
  [status in Status]: {
    className: keyof typeof styles;
    icon: BenchlingIconName;
    tooltip: string;
  };
} = {
  [Status.PENDING]: {
    className: 'pendingIcon',
    icon: 'request-pending',
    tooltip: 'Not started',
  },
  [Status.RUNNING]: {
    className: 'runningIcon',
    icon: 'request-in-progress',
    tooltip: 'Running',
  },
  [Status.SUCCEEDED]: {
    className: 'succeededIcon',
    icon: 'request-executed',
    tooltip: 'Completed',
  },
  [Status.FAILED]: {
    className: 'failedIcon',
    icon: 'status-failed',
    tooltip: 'Error',
  },
};
interface StatusIconProps {
  status: Status;
}

function StatusIcon(props: StatusIconProps): JSX.Element {
  const statusIconInfo = STATUS_ICONS[props.status];
  return (
    <Tooltip title={statusIconInfo.tooltip}>
      <BenchlingIcon
        className={classNames(css(styles.statusIcon, styles[statusIconInfo.className]))}
        icon={statusIconInfo.icon}
      />
    </Tooltip>
  );
}

interface AutomationFileStatusSectionProps {
  automationFile: AutomationOutputFile | AutomationInputFile | AutomationTransformFile | null;
  hasBorder?: boolean;
  runId: string | null;
  configName: string | null;
  locked: boolean;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFile>) => void;
  previousFileNames: Set<string>;
  onRequestClose: () => void;
  processingSummaryStatistics?: ProcessingSummaryStatistics | null;
}

function AutomationFileStatusSection(props: AutomationFileStatusSectionProps): JSX.Element | null {
  if (!props.automationFile) {
    return null;
  }
  const title = isAutomationProcessingFile(props.automationFile)
    ? snakeToCapital(props.automationFile.type.toLowerCase())
    : isAutomationOutputFile(props.automationFile)
      ? 'Process output file'
      : 'Generate input file';

  return (
    <div className={css(styles.subsection, props.hasBorder && styles.subsectionBorder)}>
      <StatusIcon status={props.automationFile.status} />
      <div
        className={css(
          styles.sectionTitle,
          props.automationFile.status === Status.PENDING && styles.pendingFont
        )}
      >
        {title}
      </div>
      {props.automationFile.status !== Status.PENDING && props.automationFile.modifiedAt && (
        <div className={css(styles.modifiedAt)}>{readableTime(props.automationFile.modifiedAt)}</div>
      )}
      {props.automationFile.status === Status.PENDING && (
        <div className={css(styles.pendingFont)}>Not started</div>
      )}
      {props.automationFile.status === Status.RUNNING && <div>Running...</div>}
      {props.automationFile.errors && (
        <AutomationFileErrors
          errors={props.automationFile.errors}
          schemaIds={getSchemaIds(props.automationFile)}
        />
      )}
      {isAutomationOutputFile(props.automationFile) && (
        <CompletedWithErrorsSection
          automationOutputFile={props.automationFile}
          runId={props.runId}
          configName={props.configName}
          locked={props.locked}
          updateAutomationOutputFiles={props.updateAutomationOutputFiles}
          previousFileNames={props.previousFileNames}
          onRequestClose={props.onRequestClose}
        />
      )}
      {BenchlingConfig.ENABLE_LAB_AUTOMATION_FILE_CHUNKING &&
        props.automationFile.status === Status.RUNNING &&
        isAutomationOutputFile(props.automationFile) &&
        props.processingSummaryStatistics && (
          <AutomationFileProgressBar processingSummaryStatistics={props.processingSummaryStatistics} />
        )}
      {!isAutomationOutputFile(props.automationFile) && props.automationFile.blob && (
        <div>
          <Chip
            className="chip-container"
            icon="paperclip"
            size={ChipSize.MINI}
            text={props.automationFile.blob.name}
            href={props.automationFile.blob.url}
          />
        </div>
      )}
    </div>
  );
}

function CancelProcessingButton(props: {
  disabled: boolean;
  automationFile: AutomationOutputFile | AutomationInputFile;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFile>) => void;
  updateAutomationInputFile: (automationInputFile: AutomationInputFile) => void;
}): JSX.Element {
  return (
    <span
      className={css(!props.disabled ? styles.cancelButton : styles.disabledCancelButton)}
      onClick={
        !props.disabled
          ? async () => {
              const confirm = await Promise.resolve(
                modals.confirm(
                  'Cancel file processing',
                  'Are you sure you want to cancel processing this file?',
                  ButtonStyle.PRIMARY,
                  {
                    acceptText: 'Cancel',
                    rejectText: 'Back',
                    enableRedesign: true,
                    promiseCompatible: true,
                  }
                )
              );
              if (!confirm) {
                return;
              }
              const response = await AutomationFileDAO.cancelCustomTransform(props.automationFile.id!);
              // eslint-disable-next-line benchlint/no-in-operator
              if ('automationOutputFile' in response) {
                props.updateAutomationOutputFiles([response.automationOutputFile]);
              } else {
                props.updateAutomationInputFile(response.automationInputFile);
              }
            }
          : undefined
      }
    >
      Cancel file processing
    </span>
  );
}

interface CompletedWithErrorsSectionProps {
  automationOutputFile: AutomationOutputFile;
  runId: string | null;
  configName: string | null;
  locked: boolean;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFile>) => void;
  previousFileNames: Set<string>;
  onRequestClose: () => void;
}

function CompletedWithErrorsSection(props: CompletedWithErrorsSectionProps): JSX.Element | null {
  const _handleUploadAmendedErrorsFile = (): void => {
    props.onRequestClose();
  };

  if (!props.automationOutputFile.rowLevelErrorsBlob || !props.automationOutputFile.numRowsWithErrors) {
    return null;
  }

  let errorText = String(props.automationOutputFile.numRowsWithErrors);
  if (props.automationOutputFile?.numRowsWithErrors > 1) {
    errorText += ' rows were skipped due to errors.';
  } else {
    errorText += ' row was skipped due to errors.';
  }
  return (
    <>
      <span className={css(styles.numRowsWithErrors)}>{errorText}</span>
      <Chip
        className={css(styles.rowLevelErrorsBlob)}
        icon="paperclip"
        size={ChipSize.MINI}
        text={props.automationOutputFile.rowLevelErrorsBlob.name}
        href={props.automationOutputFile.rowLevelErrorsBlob.url}
      />
      <div className={css(styles.errorFileInstructions)}>
        The file above contains skipped rows and error messages. To try again, correct the records and upload
        rectified file below. (Benchling will ignore 'File errors' column when processing this file.)
      </div>
      <Row>
        <Col md={3}>
          <span className="fieldLabel-sublabel">Error file</span>
        </Col>
        <Col md={6}>
          <UploadAutomationOutputFileButton
            runId={props.runId}
            isConnectionRun={false}
            connectionId={null}
            configName={props.configName}
            disabled={props.locked || !props.runId}
            updateAutomationOutputFiles={props.updateAutomationOutputFiles}
            previousFileNames={props.previousFileNames}
            outputFileType={AutomationOutputFileType.AMENDED_ERRORS_FILE}
            onRequestClose={_handleUploadAmendedErrorsFile}
          />
        </Col>
      </Row>
    </>
  );
}

interface AutomationFileStatusModalProps {
  automationFile: AutomationOutputFile | AutomationInputFile;
  runId: string | null;
  configName: string | null;
  locked: boolean;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFile>) => void;
  updateAutomationInputFile: (automationInputFiles: AutomationInputFile) => void;
  previousFileNames: Set<string>;
  onRequestClose: () => void;
  processingSummaryStatistics?: ProcessingSummaryStatistics | null;
  editorPropertiesContext: EditorPropertiesContextInterface;
}

function getTransformFilesFromAutomationFile(
  automationFile: AutomationOutputFile | AutomationInputFile
): Array<AutomationTransformFile | null> {
  if (isAutomationOutputFile(automationFile)) {
    return automationFile.transforms ?? [];
  }
  return automationFile.transformFiles ?? [];
}

function AutomationFileStatusModal(props: AutomationFileStatusModalProps): JSX.Element {
  const transforms = getTransformFilesFromAutomationFile(props.automationFile);
  const customTransformProcessingFiles = transforms.filter(
    (transform) => transform && transform.type === LabAutomationTransformType.CUSTOM
  ) as Array<AutomationTransformFile>;
  const hasRunningCustomTransform = customTransformProcessingFiles.some(
    (transform) => transform.status === Status.RUNNING
  );

  const automationFileStatusSection = (
    <AutomationFileStatusSection
      automationFile={props.automationFile}
      runId={props.runId}
      configName={props.configName}
      locked={props.locked}
      updateAutomationOutputFiles={props.updateAutomationOutputFiles}
      previousFileNames={props.previousFileNames}
      onRequestClose={props.onRequestClose}
      processingSummaryStatistics={props.processingSummaryStatistics}
    />
  );
  return (
    <EditorPropertiesContext.Provider value={props.editorPropertiesContext}>
      <Modal
        title="File processing details"
        onRequestClose={props.onRequestClose}
        buttons={[
          {
            key: 'close',
            text: 'Close',
            style: ButtonStyle.SECONDARY,
            onClick: props.onRequestClose,
          },
        ]}
        footer={
          customTransformProcessingFiles.length ? (
            <CancelProcessingButton
              disabled={!hasRunningCustomTransform}
              automationFile={props.automationFile}
              updateAutomationOutputFiles={props.updateAutomationOutputFiles}
              updateAutomationInputFile={props.updateAutomationInputFile}
            />
          ) : null
        }
      >
        {isAutomationInputFile(props.automationFile) ? automationFileStatusSection : null}
        {transforms.map((transform, i) => (
          <AutomationFileStatusSection
            key={i}
            automationFile={transform}
            hasBorder={true}
            runId={props.runId}
            configName={props.configName}
            locked={props.locked}
            updateAutomationOutputFiles={props.updateAutomationOutputFiles}
            previousFileNames={props.previousFileNames}
            onRequestClose={props.onRequestClose}
          />
        ))}
        {isAutomationOutputFile(props.automationFile) ? automationFileStatusSection : null}
      </Modal>
    </EditorPropertiesContext.Provider>
  );
}

export function runAutomationInputFileStatusModal(props: {
  automationFile: AutomationInputFile;
  runId: string | null;
  locked: boolean;
  updateAutomationInputFile: (automationInputFile: AutomationInputFile) => Promise<void>;
  editorPropertiesContext: EditorPropertiesContextInterface;
}): void {
  runModal(AutomationFileStatusModal, {
    ...props,
    configName: props.automationFile.automationInputFileConfig.name,
    updateAutomationOutputFiles: noop,
    previousFileNames: new Set<string>(),
  });
}

export function runAutomationOutputFileStatusModal(props: {
  automationFile: AutomationOutputFile;
  runId: string | null;
  locked: boolean;
  configName: string | null;
  updateAutomationOutputFiles: (automationOutputFiles: Array<AutomationOutputFile>) => void;
  previousFileNames: Set<string>;
  processingSummaryStatistics?: ProcessingSummaryStatistics | null;
  editorPropertiesContext: EditorPropertiesContextInterface;
}): void {
  runModal(AutomationFileStatusModal, {
    ...props,
    updateAutomationInputFile: noop,
  });
}

export default AutomationFileStatusModal;

const styles = StyleSheet.create({
  subsection: {
    position: 'relative',
    marginLeft: '24px',
    paddingLeft: '24px',
    paddingBottom: '24px',
  },
  subsectionBorder: {
    borderLeft: `1px solid ${Colors.GRAY}`,
  },
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
  },
  statusIcon: {
    position: 'absolute',
    zIndex: 30,
    top: '4px',
    left: '-6px',
    float: 'left',
    boxShadow: `0 0 0 4px ${Colors.WHITE}`,
    backgroundColor: `${Colors.WHITE}`,
  },
  succeededIcon: {
    color: Colors.GREEN,
  },
  failedIcon: {
    color: Colors.RED,
  },
  pendingIcon: {
    color: Colors.GRAY,
  },
  runningIcon: {
    color: Colors.BLUE_LIGHT,
  },
  modifiedAt: {
    color: Colors.GRAY_DARK,
    fontSize: 11,
  },
  pendingFont: {
    color: Colors.GRAY_DARK,
  },
  cancelButton: {
    color: Colors.RED_DARK,
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: '24px',
  },
  disabledCancelButton: {
    color: Colors.GRAY_DARK,
    fontWeight: 600,
    cursor: 'not-allowed',
    marginLeft: '24px',
  },
  numRowsWithErrors: {
    color: Colors.RED,
  },
  rowLevelErrorsBlob: {
    marginTop: '5px',
  },
  errorFileInstructions: {
    marginTop: '24px',
    marginBottom: '24px',
    color: Colors.GRAY_DARKER,
  },
});
